const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_PATTERN = /(?<!\w)(?:\+?\d[\d\s().-]{7,}\d)(?!\w)/g;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~-]+/gi;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const LONG_SECRET_PATTERN = /\b[A-Za-z0-9_-]{32,}\b/g;
const URL_PATTERN = /https?:\/\/[^\s)\]}]+/gi;

const FILTERED = '[Filtered]';
const MAX_DEPTH = 8;
const SENSITIVE_KEY_PATTERN = /(?:authorization|cookie|password|passwd|secret|token|api[_-]?key|session|email|phone|mobile|address|first[_-]?name|last[_-]?name|full[_-]?name)/i;

function sanitiseUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return rawUrl.split(/[?#]/, 1)[0] || FILTERED;
  }
}

export function redactTelemetryText(value: string): string {
  return value
    .replace(URL_PATTERN, (url) => sanitiseUrl(url))
    .replace(BEARER_PATTERN, `Bearer ${FILTERED}`)
    .replace(JWT_PATTERN, FILTERED)
    .replace(EMAIL_PATTERN, FILTERED)
    .replace(PHONE_PATTERN, FILTERED)
    .replace(LONG_SECRET_PATTERN, FILTERED);
}

function scrubValue(value: unknown, key: string | null, depth: number, seen: WeakSet<object>): unknown {
  if (value == null || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (key && SENSITIVE_KEY_PATTERN.test(key)) return FILTERED;
    return redactTelemetryText(value);
  }
  if (typeof value !== 'object') return String(value);
  if (depth >= MAX_DEPTH) return '[Truncated]';
  if (seen.has(value)) return '[Circular]';

  seen.add(value);
  if (Array.isArray(value)) {
    const scrubbed = value.map((item) => scrubValue(item, key, depth + 1, seen));
    seen.delete(value);
    return scrubbed;
  }

  const result: Record<string, unknown> = {};
  for (const [childKey, childValue] of Object.entries(value)) {
    if (SENSITIVE_KEY_PATTERN.test(childKey)) {
      result[childKey] = FILTERED;
      continue;
    }
    result[childKey] = scrubValue(childValue, childKey, depth + 1, seen);
  }
  seen.delete(value);
  return result;
}

/**
 * Remove customer and credential data before an event leaves the browser.
 * The function is intentionally generic so it can protect errors, transactions,
 * breadcrumbs and future Sentry event shapes without coupling to an SDK version.
 */
export function scrubSentryEvent<T>(event: T): T {
  const scrubbed = scrubValue(event, null, 0, new WeakSet<object>()) as T;
  if (!scrubbed || typeof scrubbed !== 'object') return scrubbed;

  const record = scrubbed as Record<string, unknown>;
  delete record.user;
  delete record.extra;

  const request = record.request;
  if (request && typeof request === 'object' && !Array.isArray(request)) {
    const safeRequest = request as Record<string, unknown>;
    if (typeof safeRequest.url === 'string') safeRequest.url = sanitiseUrl(safeRequest.url);
    delete safeRequest.data;
    delete safeRequest.cookies;
    delete safeRequest.headers;
    delete safeRequest.env;
    delete safeRequest.query_string;
    delete safeRequest.fragment;
  }

  return scrubbed;
}
